import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { FinishSnapshot } from '../../api/useGroupFinish';
import { formatUsd, pnlColor } from '../../utils/format';

const SummaryCard = ({
  title,
  value,
  color
}: {
  title: string;
  value: string;
  color: string;
}) => (
  <Paper variant="outlined" sx={{ p: 1.5, minWidth: 110, flex: 1 }}>
    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color }}>
      {value}
    </Typography>
  </Paper>
);

export const FinishReportPanel = ({ snapshot }: { snapshot: FinishSnapshot }) => {
  const { totals, counts, markets, anomalies } = snapshot;

  const calzadaCount = markets.filter(m => m.hedge.class === 'CALZADA').length;
  const descalzadaCount = markets.filter(m => m.hedge.class === 'DESCALZADA').length;
  const nakedCount = markets.filter(m => m.hedge.class === 'NAKED_TOTAL').length;

  const anomalyEntries = Object.entries({
    'naked remainder': anomalies.nakedRemainder,
    'counter stuck': anomalies.counterStuck,
    'hedge deferred': anomalies.hedgeDeferred,
    errors: anomalies.errors,
    panics: anomalies.panics,
    'trading halted': anomalies.tradingHalted,
    'duplicate rules': anomalies.duplicateRules
  }).filter(([, count]) => count > 0);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <SummaryCard
          title="Resolved Total"
          value={formatUsd(totals.groupNetUsd)}
          color={pnlColor(totals.groupNetUsd)}
        />
        <SummaryCard
          title="Realized Cash"
          value={formatUsd(totals.realizedCashUsd)}
          color={pnlColor(totals.realizedCashUsd)}
        />
        <SummaryCard
          title="Kalshi Net"
          value={formatUsd(totals.kalshiNetUsd)}
          color={pnlColor(totals.kalshiNetUsd)}
        />
        <SummaryCard
          title="Poly Net"
          value={formatUsd(totals.polymarketNetUsd)}
          color={pnlColor(totals.polymarketNetUsd)}
        />
      </Stack>

      {totals.polyUnredeemedCash > 0 && (
        <Typography sx={{ fontSize: '0.75rem', color: 'warning.main' }}>
          Cash pending redemption: {formatUsd(totals.polyUnredeemedCash)}
        </Typography>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Market</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Resolution</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Kalshi side</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Net</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Kalshi / Poly</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {markets.map((market, idx) => (
              <TableRow key={market.pairId}>
                <TableCell sx={{ fontSize: '0.75rem' }}>{idx + 1}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', maxWidth: 180 }}>
                  <Typography noWrap sx={{ fontSize: '0.75rem' }}>
                    {market.pairName}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {market.resolution === 'unresolved' ? (
                      <Chip label="UNRESOLVED" color="error" size="small" />
                    ) : (
                      <Chip
                        label={market.resolution.toUpperCase()}
                        color={market.resolution === 'yes' ? 'success' : 'default'}
                        size="small"
                      />
                    )}
                    {market.polymarketLeg.redeemed === false &&
                      market.resolution !== 'unresolved' && (
                        <Chip label="poly unredeemed" color="warning" size="small" />
                      )}
                    {(market.kalshiLeg.side === 'unconfirmed' ||
                      market.polymarketLeg.side === 'unconfirmed') && (
                      <Chip label="leg unconfirmed" color="warning" size="small" />
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{market.kalshiLeg.side}</TableCell>
                <TableCell
                  sx={{ fontSize: '0.75rem', color: pnlColor(market.netUsd), fontWeight: 500 }}
                >
                  {formatUsd(market.netUsd)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>
                  {formatUsd(market.kalshiLeg.netUsd)} / {formatUsd(market.polymarketLeg.netUsd)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack spacing={0.5}>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {counts.marketsWithFills} filled · {counts.marketsNeverFilled} never filled ·{' '}
          {counts.unresolvedMarkets} unresolved
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          CALZADA: {calzadaCount} | DESCALZADA: {descalzadaCount} | NAKED: {nakedCount}
        </Typography>
      </Stack>

      {anomalyEntries.length > 0 && (
        <Box>
          <Typography
            sx={{ fontSize: '0.7rem', color: 'warning.main', fontWeight: 600, mb: 0.25 }}
          >
            Anomalies
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'warning.main' }}>
            {anomalyEntries.map(([label, count]) => `${label}: ${count}`).join(', ')}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
